import { Component, OnInit, OnDestroy, Inject, Renderer2, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ClientsTestimonalComponent } from '../../general/clients-testimonal/clients-testimonal.component';
import { GalleryComponent } from '../../general/gallery/gallery.component';
import { SuiteTechnologiesComponent } from '../../general/suite-technologies/suite-technologies.component';
import { RecentUpdatesComponent } from '../../general/recent-updates/recent-updates.component';
import { trigger, style, animate, transition, state, query, stagger } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../contact-us/contact-us.component';

export interface Industry {
  imageSrc: string;
  iconSrc: string;
  title: string;
  description: string;
  route: string;
}
type TemplateItem = Industry | { placeholder: true };

export interface Service {
  imageSrc: string;
  iconSrc: string;
  title: string;
  description: string;
  route: string;
}
type ServiceTemplateItem = Service | { placeholder: true };

@Component({
  selector: 'app-home',
  imports: [CommonModule, ContactUsComponent, ClientsTestimonalComponent, GalleryComponent, SuiteTechnologiesComponent, RecentUpdatesComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  standalone: true,
  animations: [
    // ---------------- INDUSTRIES ANIMATIONS ----------------
    trigger('industryImageAnim', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX({{startOffset}}) scale(0.9)',
          zIndex: '{{zIndex}}'
        }),
        animate('1500ms {{delay}}ms cubic-bezier(0.2, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'translateX(0) scale(1)' })
        )
      ], { params: { startOffset: '0%', delay: 0, zIndex: 1 } })
    ]),

    trigger('industryTextAnim', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY({{startOffset}})',
          zIndex: '{{zIndex}}'
        }),
        animate('1500ms {{delay}}ms cubic-bezier(0.2, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'translateY(0)' })
        )
      ], { params: { startOffset: '0%', delay: 0, zIndex: 1 } })
    ]),

    // ---------------- SERVICES ANIMATIONS ----------------
    trigger('serviceImageAnim', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX({{startOffset}}) scale(0.9)',
          zIndex: '{{zIndex}}'
        }),
        animate('1500ms {{delay}}ms cubic-bezier(0.2, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'translateX(0) scale(1)' })
        )
      ], { params: { startOffset: '0%', delay: 0, zIndex: 1 } })
    ]),

    trigger('serviceTextAnim', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY({{startOffset}})',
          zIndex: '{{zIndex}}'
        }),
        animate('1500ms {{delay}}ms cubic-bezier(0.2, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'translateY(0)' })
        )
      ], { params: { startOffset: '0%', delay: 0, zIndex: 1 } })
    ]),

    trigger('metricsAnim', [
      transition(':enter', [
        query('.metric-item', [
          style({ opacity: 0, transform: 'translateY(50px)' })
        ], { optional: true }),

        query('.metric-item', [
          stagger('300ms', [
            animate('1500ms cubic-bezier(0.35, 0, 0.25, 1)',
              style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ]),
      transition(':leave', [
        query('.metric-item', [
          stagger('100ms', [
            animate('500ms ease-out',
              style({ opacity: 0, transform: 'translateY(50px)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})

export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('milestoneVideo') milestoneVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('heroVideo', { static: true }) heroVideo!: ElementRef<HTMLVideoElement>;

  private lazyLoadObserver: IntersectionObserver | null = null;
  private animationObserver: IntersectionObserver | null = null;
  private videoObserver: IntersectionObserver | null = null;
  private navigationObserver: IntersectionObserver | null = null;
  private servicesInitialized = false;

  loadedSections: Set<string> = new Set(['hero', 'clients']);

  sections = [
    { id: 'hero', label: 'Hero Banner' },
    { id: 'clients', label: 'Clients' },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'milestones', label: 'Milestones' },
    { id: 'industries', label: 'Industries' },
    { id: 'technology', label: 'Technology' },
    { id: 'about', label: 'About Us' },
    { id: 'services', label: 'Services' },
    { id: 'recentupdates', label: 'News/Blog/WhitePaper' },
    { id: 'gallery', label: 'Gallery' }
  ];

  allIndustries: Industry[] = [
    {
      imageSrc: 'assets/home/industries/healthcare_image.png',
      iconSrc: 'assets/home/industries/healthcare_icon.webp',
      title: 'HealthTech',
      description: 'We create compliant HealthTech systems that streamline patient care, automate records, and improve clinical outcomes.',
      route: 'industries/healthtech'
    },
    {
      imageSrc: 'assets/home/industries/media_img.png',
      iconSrc: 'assets/home/industries/media_icon.webp',
      title: 'Media & Entertainment',
      description: 'We develop immersive media solutions including OTT, streaming, and content platforms built for speed, scale, and engagement.',
      route: 'industries/media-entertainment'
    },
    {
      imageSrc: 'assets/home/industries/ecommerce_img.png',
      iconSrc: 'assets/home/industries/ecommerce_icon.webp',
      title: 'E-commerce',
      description: 'We power scalable e-commerce platforms with smart automation, optimized UX, and secure, high-performance integrations.',
      route: 'industries/ecommerce'
    },
    {
      imageSrc: 'assets/home/industries/insurance_img.png',
      iconSrc: 'assets/home/industries/insurance_icon.webp',
      title: 'InsurTech',
      description: 'We build AI-driven insurance platforms that automate workflows, boost efficiency, and improve policyholder experiences.',
      route: 'industries/insurtech'
    },
    {
      imageSrc: 'assets/home/industries/finance_img.png',
      iconSrc: 'assets/home/industries/finance_icon.webp',
      title: 'FinTech',
      description: 'We engineer secure FinTech solutions that accelerate data accuracy, automate reporting, and enhance financial decision-making.',
      route: 'industries/fintech'
    },
    {
      imageSrc: 'assets/home/industries/elearning_img.png',
      iconSrc: 'assets/home/industries/elearning_icon.webp',
      title: 'EdTech',
      description: 'We build interactive EdTech platforms with personalized learning, analytics, and seamless digital classroom experiences.',
      route: 'industries/edtech'
    },
    {
      imageSrc: 'assets/home/industries/hi-tech-img.png',
      iconSrc: 'assets/home/industries/hi-tech-icon.webp',
      title: 'Hi-Tech ',
      description: 'We deliver next-gen Hi-Tech software with AI, automation, and cloud engineering to accelerate innovation and product growth.',
      route: 'industries/hitech'
    }
  ];

  allServices: Service[] = [
    {
      imageSrc: 'assets/home/services/softwaredev.png',
      iconSrc: 'assets/home/services/software-dev.webp',
      title: 'Custom Software Development',
      description: 'We build custom software solutions to meet unique business needs, ensuring scalability, efficiency, and long-term growth.',
      route: 'company-services/custom-software-development'
    },
    {
      imageSrc: 'assets/home/services/webdev.png',
      iconSrc: 'assets/home/services/web-dev.webp',
      title: 'Web Development',
      description: 'Our web development services deliver responsive, secure, and SEO-optimized websites that enhance online presence and engagement.',
      route: 'company-services/web-development'
    },
    {
      imageSrc: 'assets/home/services/mbldev.png',
      iconSrc: 'assets/home/services/mbl-dev.webp',
      title: 'Mobile App Development',
      description: 'We create intuitive, high-performance mobile apps for iOS and Android that drive user engagement and business value.',
      route: 'company-services/mobile-app-development'
    },
    {
      imageSrc: 'assets/home/services/ui-ux-img.png',
      iconSrc: 'assets/home/services/ui-ux.webp',
      title: 'UI/UX Design',
      description: 'Our UI/UX design services focus on intuitive, user-friendly interfaces that improve customer experience and satisfaction.',
      route: 'company-services/ui-ux-design'
    },
    {
      imageSrc: 'assets/home/services/data-analytics-img.png',
      iconSrc: 'assets/home/services/data-analytics.webp',
      title: 'Data Engineering',
      description: 'Our data analytics services help businesses extract insights, improve decision-making, and drive measurable business outcomes.',
      route: 'company-services/data-engineering'
    },
    {
      imageSrc: 'assets/home/services/qa-img.png',
      iconSrc: 'assets/home/services/qa.webp',
      title: 'Testing & QA',
      description: 'We ensure software quality and reliability through end-to-end testing and QA processes, minimizing risks and downtime.',
      route: 'company-services/testing-qa'
    },
    {
      imageSrc: 'assets/home/services/it-consulting.png',
      iconSrc: 'assets/home/services/it-consultant.webp',
      title: 'IT Consulting',
      description: 'Our IT consulting services guide businesses with the right strategies, technologies, and roadmaps for digital success.',
      route: 'company-services/it-consulting'
    },
    {
      imageSrc: 'assets/home/services/ai-as-a-service.png',
      iconSrc: 'assets/home/services/AI-as-service.webp',
      title: 'AI as a Service',
      description: 'We offer AI-powered solutions that automate processes, enhance decision-making, and deliver smarter business outcomes.',
      route: 'company-services/ai-as-a-service'
    },
    {
      imageSrc: 'assets/home/services/digital-marketing-img.png',
      iconSrc: 'assets/home/services/digital-marketing.webp',
      title: 'Digital Marketing',
      description: 'Our digital marketing services help businesses improve visibility, attract leads, and drive measurable online growth.',
      route: 'company-services/digital-marketing'
    }
  ];

  industriesVisible = false;
  servicesVisible = false;
  metricsVisible = false;

  private jsonLdScript: any;

  // Pagination vars
  paginatedIndustries: Industry[][] = [];
  templateItems: TemplateItem[] = [];
  activeSlideIndex = 0;
  private slideInterval: any;

  paginatedServices: Service[][] = [];
  templateServiceItems: ServiceTemplateItem[] = [];
  activeServiceSlideIndex = 0;
  private serviceSlideInterval: any;

  activeSection: string = 'hero';
  hoveredSection: string | null = null;
  hoveredIndustryIndex: number | null = null;
  hoveredServiceIndex: number | null = null;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal
  ) { }

  navigateTo(path: string): void {
    if (path) {
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;
      const routeSegments = ['/', ...cleanPath.split('/')];

      console.log('Navigating to:', routeSegments);

      this.router.navigate(routeSegments).then(success => {
        if (success) {
          window.scrollTo(0, 0);
        } else {
          console.error('Navigation failed. Check route config for:', cleanPath);
        }
      });
    }
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();

    // 1. Prepare Industries Pagination
    for (let i = 0; i < this.allIndustries.length; i += 3) {
      this.paginatedIndustries.push(this.allIndustries.slice(i, i + 3));
    }
    // Set initial data with placeholders
    const firstIndustryPage = this.paginatedIndustries[0];
    const industryTemplateItems: TemplateItem[] = [...firstIndustryPage];
    const industryPlaceholders = 3 - firstIndustryPage.length;
    for (let i = 0; i < industryPlaceholders; i++) {
      industryTemplateItems.push({ placeholder: true });
    }
    this.templateItems = industryTemplateItems;

    // 2. Prepare Services Pagination
    for (let i = 0; i < this.allServices.length; i += 3) {
      this.paginatedServices.push(this.allServices.slice(i, i + 3));
    }
    // Set initial data with placeholders
    const firstServicePage = this.paginatedServices[0];
    const serviceTemplateItems: ServiceTemplateItem[] = [...firstServicePage];
    const servicePlaceholders = 3 - firstServicePage.length;
    for (let i = 0; i < servicePlaceholders; i++) {
      serviceTemplateItems.push({ placeholder: true });
    }
    this.templateServiceItems = serviceTemplateItems;

    // Fragment Scroll Listener
    this.route.fragment.subscribe((fragment) => {
      if (fragment) {
        this.loadedSections.add(fragment);
        setTimeout(() => {
          this.scrollToSection(fragment);
        }, 100);
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const video = this.heroVideo?.nativeElement;
      if (video) {
        video.muted = true;
        video.playsInline = true;
        video.load();
        video.play().catch(() => {
          console.warn('Autoplay blocked – retrying...');
          video.play();
        });
      }
    }, 100);
    
    if ('IntersectionObserver' in window) {
      this.setupLazyLoadObserver();
      this.setupAnimationObserver();
      this.setupVideoObserver();
      this.setupNavigationObserver();
    } else {
      // Fallback for older browsers
      this.sections.forEach(s => this.loadedSections.add(s.id));
      this.industriesVisible = true;
      this.servicesVisible = true;
      this.metricsVisible = true;
      this.startAutoSlide();
      this.startAutoServiceSlide();
    }
  }

  // 1. Lazy Load Observer (Buffer for loading heavy components)
  setupLazyLoadObserver() {
    const options = {
      root: null,
      threshold: 0.35,
      rootMargin: '0px 0px -20% 0px'
    };

    this.lazyLoadObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          if (id === 'testimonialsObserverTarget') this.loadedSections.add('testimonials');
          if (id === 'technologyObserverTarget') this.loadedSections.add('technology');
          if (id === 'recentupdatesObserverTarget') this.loadedSections.add('recentupdates');
          if (id === 'galleryObserverTarget') this.loadedSections.add('gallery');

          this.lazyLoadObserver?.unobserve(entry.target);
        }
      });
    }, options);

    const targets = [
      'testimonialsObserverTarget', 'technologyObserverTarget',
      'recentupdatesObserverTarget', 'galleryObserverTarget'
    ];

    targets.forEach(t => {
      const el = document.getElementById(t);
      if (el) this.lazyLoadObserver?.observe(el);
    });
  }

  // 2. Animation & Slider Reset Observer (FIXED VERSION)
  setupAnimationObserver() {
    const options = {
      root: null,
      threshold: 0.15,
      rootMargin: '50px 0px 50px 0px' // Buffer zone to prevent flickering
    };

    this.animationObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const id = entry.target.id;

        if (entry.isIntersecting) {

          // --- INDUSTRIES SECTION ---
          if (id === 'industries') {
            if (!this.industriesVisible) {
              this.industriesVisible = true;
              setTimeout(() => {
                this.selectSlide(0);
              }, 150);
            } else if (!this.slideInterval) {
              // Resume if paused
              this.startAutoSlide();
            }
          }

          // --- SERVICES SECTION ---
          if (id === 'services') {
            if (!this.servicesInitialized) {
              this.servicesInitialized = true;
              this.servicesVisible = true;
              setTimeout(() => {
                this.selectServiceSlide(0);
                this.startAutoServiceSlide();
              }, 150);
            } else if (!this.serviceSlideInterval) {
              // Resume if paused
              this.startAutoServiceSlide();
            }
          }

          // --- METRICS TRIGGER ---
          if (id === 'metrics-trigger') {
            if (!this.metricsVisible) {
              this.metricsVisible = true;
            }
          }

        } else {
          // When elements leave viewport - ONLY pause timers, NEVER hide
          
          if (id === 'industries') {
            this.clearAutoSlide();
          }
          
          if (id === 'services') {
            this.clearAutoServiceSlide();
          }
          
          // IMPORTANT: Don't hide metrics-trigger anymore
          // Keep it visible once shown to prevent re-animation
        }
      });
    }, options);

    const targets = ['industries', 'services', 'metrics-trigger'];

    targets.forEach(t => {
      const el = document.getElementById(t);
      if (el) this.animationObserver?.observe(el);
    });
  }

  // 3. Video Observer
  setupVideoObserver() {
    if (!this.milestoneVideo?.nativeElement) return;

    const options = {
      root: null,
      threshold: 0.35,
      rootMargin: '0px 0px -20% 0px'
    };

    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = this.milestoneVideo.nativeElement;
        if (entry.isIntersecting) {
          video.muted = true;
          video.currentTime = 0;
          video.play().catch(e => console.error("Video play failed", e));
        } else {
          video.pause();
        }
      });
    }, options);

    this.videoObserver.observe(this.milestoneVideo.nativeElement);
  }

  // 4. Navigation Observer
  setupNavigationObserver() {
    const options = {
      root: null,
      threshold: 0.35,
      rootMargin: '0px 0px -20% 0px'
    };

    this.navigationObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          const sectionMap: { [key: string]: string } = {
            'heroObserverTarget': 'hero',
            'clientsObserverTarget': 'clients',
            'testimonialsObserverTarget': 'testimonials',
            'milestonesObserverTarget': 'milestones',
            'industries': 'industries',
            'technologyObserverTarget': 'technology',
            'about': 'about',
            'metrics-trigger': 'about',
            'services': 'services',
            'recentupdatesObserverTarget': 'recentupdates',
            'galleryObserverTarget': 'gallery'
          };

          const sectionId = sectionMap[id] || id;

          if (this.sections.some(s => s.id === sectionId)) {
            this.activeSection = sectionId;
          }
        }
      });
    }, options);

    const navTargets = [
      'heroObserverTarget', 'clientsObserverTarget', 'testimonialsObserverTarget',
      'milestonesObserverTarget', 'industries', 'technologyObserverTarget',
      'about', 'services', 'recentupdatesObserverTarget', 'galleryObserverTarget'
    ];

    navTargets.forEach(t => {
      const el = document.getElementById(t);
      if (el) this.navigationObserver?.observe(el);
    });
  }

  shouldLoadSection(sectionId: string): boolean {
    return this.loadedSections.has(sectionId);
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.clearAutoSlide();
    this.clearAutoServiceSlide();
    if (this.lazyLoadObserver) this.lazyLoadObserver.disconnect();
    if (this.animationObserver) this.animationObserver.disconnect();
    if (this.videoObserver) this.videoObserver.disconnect();
    if (this.navigationObserver) this.navigationObserver.disconnect();
  }

injectSchemaMarkup(): void {
    const fullGraphSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["ProfessionalService", "Organization"],
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "legalName": "Kalpita Technologies Private Limited",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "@id": "https://www.kalpitatechnologies.com/#logo",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png",
            "contentUrl": "https://www.kalpitatechnologies.com/assets/common/black-logo.png",
            "caption": "Kalpita Technologies Logo"
          },
          "image": { "@id": "https://www.kalpitatechnologies.com/#logo" },
          "description": "Kalpita Technologies is an AI-first software development and SaaS company headquartered in Bangalore, India, with offices in Austin, Texas, USA. Specializing in custom software development, enterprise AI solutions, product engineering, digital transformation, and AI-powered SaaS products including Kalpita Nexa, AI Interview, and Recruit App. Serving 70+ global clients with 130+ skilled engineers and 7+ years of expertise across InsurTech, FinTech, HealthTech, EdTech, E-commerce, Media & Entertainment, and Hi-Tech verticals.",
          "slogan": "Intelligent Solutions, Infinite Possibilities",
          "foundingDate": "2018",
          "email": "connect@kalpitatechnologies.com",
          "telephone": "+91-87620-33990",
          "priceRange": "$$",
          "areaServed": [
            { "@type": "Country", "name": "India" },
            { "@type": "Country", "name": "United States" },
            { "@type": "Country", "name": "United Kingdom" },
            { "@type": "City", "name": "Bengaluru" },
            { "@type": "City", "name": "Austin" }
          ],
          "address": [
            {
              "@type": "PostalAddress",
              "@id": "https://www.kalpitatechnologies.com/#address-india",
              "streetAddress": "4M 417/A, 4th Floor, HRBR Layout 3rd Block, Kammanahalli main road",
              "addressLocality": "Bengaluru",
              "addressRegion": "Karnataka",
              "postalCode": "560043",
              "addressCountry": "IN"
            },
            {
              "@type": "PostalAddress",
              "@id": "https://www.kalpitatechnologies.com/#address-usa",
              "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
              "addressLocality": "Austin",
              "addressRegion": "TX",
              "postalCode": "78746",
              "addressCountry": "US"
            }
          ],
          "contactPoint": [
            { "@type": "ContactPoint", "@id": "https://www.kalpitatechnologies.com/#contact-india", "telephone": "+91-87620-33990", "contactType": "customer service", "email": "connect@kalpitatechnologies.com", "areaServed": "IN", "availableLanguage": ["English", "Hindi", "Kannada"] },
            { "@type": "ContactPoint", "@id": "https://www.kalpitatechnologies.com/#contact-usa", "telephone": "+1-512-640-3251", "contactType": "customer service", "email": "connect@kalpitatechnologies.com", "areaServed": "US", "availableLanguage": ["English"] },
            { "@type": "ContactPoint", "@id": "https://www.kalpitatechnologies.com/#contact-sales", "telephone": "+91-89040-87107", "contactType": "sales", "email": "sales@kalpitatechnologies.com", "availableLanguage": ["English"] },
            { "@type": "ContactPoint", "@id": "https://www.kalpitatechnologies.com/#contact-recruitment", "telephone": "+91-87620-11217", "contactType": "recruitment", "email": "recruitment@kalpitatechnologies.com", "availableLanguage": ["English"] }
          ],
          "sameAs": [
            "https://in.linkedin.com/company/kalpitatechnologies",
            "https://www.facebook.com/kalpitatechnologies",
            "https://twitter.com/KalpitaTech",
            "https://www.instagram.com/kalpitatechnologies/",
            "https://www.youtube.com/channel/UCpptWhCmPPMwNFccr_mdmrA",
            "https://www.threads.com/@kalpitatechnologies",
            "https://www.glassdoor.co.in/Reviews/Kalpita-Technologies-Reviews-E3039050.htm",
            "https://www.ambitionbox.com/reviews/kalpita-technologies-reviews",
            "https://g.page/r/CamDBGN8x9o1EAE"
          ],
          "knowsAbout": ["Artificial Intelligence", "Machine Learning", "Natural Language Processing", "Custom Software Development", "Enterprise AI Solutions", "Product Engineering", "Digital Transformation", "Cloud Computing", "DevSecOps", "Mobile App Development", "Web Application Development", "Data Analytics", "Data Engineering", "UI/UX Design", "SaaS Product Development", "HR Technology", "Recruitment Automation", "InsurTech Solutions", "FinTech Solutions", "HealthTech Solutions", "EdTech Solutions", "E-commerce Development", "Quality Assurance", "IT Consulting", "Digital Marketing", "AI as a Service"],
          "hasCredential": [
            { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "ISO 27001 Certified" },
            { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "SOC 2 Type II Compliant" },
            { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Great Place to Work Certified" }
          ],
          "award": ["Great Place to Work 2025-2026", "ISO 27001 Certified", "SOC 2 Type II Certified", "Top Digital Transformation Company – SelectedFirms", "Top Software Development Company – SelectedFirms", "GoodFirms Research Partner"],
          "numberOfEmployees": { "@type": "QuantitativeValue", "minValue": 130, "description": "130+ Skilled Engineers" },
          "aggregateRating": { "@type": "AggregateRating", "@id": "https://www.kalpitatechnologies.com/#aggregate-rating", "ratingValue": "4.9", "bestRating": "5", "worstRating": "1", "reviewCount": "7", "ratingCount": "7" }
        },
        {
          "@type": "WebSite",
          "@id": "https://www.kalpitatechnologies.com/#website",
          "url": "https://www.kalpitatechnologies.com/",
          "name": "Kalpita Technologies",
          "description": "AI-first software development and SaaS company delivering custom software, enterprise AI solutions, and digital transformation services across India and the USA.",
          "publisher": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "inLanguage": "en-US"
        },
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/#webpage",
          "url": "https://www.kalpitatechnologies.com/",
          "name": "AI-Based Software Development Company in Bangalore & USA | Kalpita Technologies",
          "description": "Kalpita Technologies is a leading AI-based software development company in Bangalore & USA offering custom software development, product engineering, digital transformation, mobile app development, AI solutions, and cloud consulting.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "about": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "primaryImageOfPage": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/home/hero-section/AI-Based-software2.gif" },
          "inLanguage": "en-US"
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/#faq",
          "mainEntity": [
            { "@type": "Question", "name": "What is Kalpita Technologies?", "acceptedAnswer": { "@type": "Answer", "text": "Kalpita Technologies is an AI-first software development and SaaS company headquartered in Bengaluru, India..." } },
            { "@type": "Question", "name": "What services does Kalpita Technologies offer?", "acceptedAnswer": { "@type": "Answer", "text": "Kalpita Technologies offers nine core services: Custom Software Development, Web Development, Mobile App Development, UI/UX Design, Data Engineering & Analytics, Testing & QA, IT Consulting, AI as a Service, and Digital Marketing." } },
            { "@type": "Question", "name": "Where is Kalpita Technologies located?", "acceptedAnswer": { "@type": "Answer", "text": "Kalpita Technologies has two offices: Bengaluru, India and Austin, Texas." } },
            { "@type": "Question", "name": "What industries does Kalpita Technologies serve?", "acceptedAnswer": { "@type": "Answer", "text": "InsurTech, FinTech, HealthTech, E-commerce, Media & Entertainment, Hi-Tech, and EdTech." } }
          ]
        },
        {
          "@type": "ItemList",
          "@id": "https://www.kalpitatechnologies.com/#industry-verticals",
          "name": "Industry Expertise",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "HealthTech", "item": "https://www.kalpitatechnologies.com/industries/healthtech" },
            { "@type": "ListItem", "position": 2, "name": "Media & Entertainment", "item": "https://www.kalpitatechnologies.com/industries/media-entertainment" },
            { "@type": "ListItem", "position": 3, "name": "E-commerce", "item": "https://www.kalpitatechnologies.com/industries/ecommerce" },
            { "@type": "ListItem", "position": 4, "name": "InsurTech", "item": "https://www.kalpitatechnologies.com/industries/insurtech" },
            { "@type": "ListItem", "position": 5, "name": "FinTech", "item": "https://www.kalpitatechnologies.com/industries/fintech" },
            { "@type": "ListItem", "position": 6, "name": "EdTech", "item": "https://www.kalpitatechnologies.com/industries/edtech" },
            { "@type": "ListItem", "position": 7, "name": "Hi-Tech", "item": "https://www.kalpitatechnologies.com/industries/hitech" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/#service-custom-software-development",
          "name": "Custom Software Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "url": "https://www.kalpitatechnologies.com/company-services/custom-software-development"
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/#service-ai-as-a-service",
          "name": "AI as a Service",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "url": "https://www.kalpitatechnologies.com/company-services/ai-as-a-service"
        },
        {
          "@type": "SoftwareApplication",
          "@id": "https://www.kalpitatechnologies.com/#product-nexa",
          "name": "Kalpita Nexa",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web-based",
          "creator": { "@id": "https://www.kalpitatechnologies.com/#organization" }
        },
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "Nouri Bakkali" },
          "reviewBody": "I am really impressed by the quality of services I received from Kalpita Technologies.",
          "itemReviewed": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "reviewRating": { "@type": "Rating", "ratingValue": "5" }
        }
      ]
    };

    this.jsonLdScript = this._renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(fullGraphSchema);
    this._renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this._renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  // --- INDUSTRIES SLIDER (FIXED) ---
  startAutoSlide(): void {
    // Don't start if already running or if section not visible
    if (this.slideInterval || !this.industriesVisible) return;
    
    this.slideInterval = setInterval(() => {
      const nextSlideIndex = (this.activeSlideIndex + 1) % this.paginatedIndustries.length;
      this.selectSlide(nextSlideIndex);
    }, 6000);
  }

  clearAutoSlide(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
      this.slideInterval = null;
    }
  }

  selectSlide(index: number): void {
    this.hoveredIndustryIndex = null;
    this.activeSlideIndex = index;
    const currentPage = this.paginatedIndustries[index];

    if (currentPage) {
      const newTemplateItems: TemplateItem[] = [...currentPage];
      const placeholdersNeeded = 3 - currentPage.length;
      if (placeholdersNeeded > 0) {
        for (let i = 0; i < placeholdersNeeded; i++) {
          newTemplateItems.push({ placeholder: true });
        }
      }
      this.templateItems = newTemplateItems;
    }

    // DON'T restart the interval here - it creates conflicts
    // The interval is already running from startAutoSlide()
  }

  setHoveredIndustry(index: number): void { 
    this.hoveredIndustryIndex = index; 
  }
  
  clearHoveredIndustry(): void { 
    this.hoveredIndustryIndex = null; 
  }
  
  isIndustry(item: TemplateItem): item is Industry { 
    return (item as Industry).route !== undefined; 
  }
  
  isPlaceholder(item: TemplateItem): item is { placeholder: true } { 
    return 'placeholder' in item; 
  }

  // --- SERVICES SLIDER (FIXED) ---
  startAutoServiceSlide(): void {
    // Don't start if already running or if section not initialized
    if (this.serviceSlideInterval || !this.servicesInitialized || !this.servicesVisible) return;

    this.serviceSlideInterval = setInterval(() => {
      this.selectServiceSlide(
        (this.activeServiceSlideIndex + 1) % this.paginatedServices.length
      );
    }, 5000);
  }

  clearAutoServiceSlide(): void {
    if (this.serviceSlideInterval) {
      clearInterval(this.serviceSlideInterval);
      this.serviceSlideInterval = null;
    }
  }

  selectServiceSlide(index: number): void {
    this.hoveredServiceIndex = null;
    this.activeServiceSlideIndex = index;
    const currentPage = this.paginatedServices[index];

    if (currentPage) {
      const newTemplateItems: ServiceTemplateItem[] = [...currentPage];
      const placeholdersNeeded = 3 - currentPage.length;
      if (placeholdersNeeded > 0) {
        for (let i = 0; i < placeholdersNeeded; i++) {
          newTemplateItems.push({ placeholder: true });
        }
      }
      this.templateServiceItems = newTemplateItems;
    }

    // DON'T restart the interval here - it creates conflicts
    // The interval is already running from startAutoServiceSlide()
  }

  setHoveredService(index: number): void { 
    this.hoveredServiceIndex = index; 
  }
  
  clearHoveredService(): void { 
    this.hoveredServiceIndex = null; 
  }
  
  isService(item: ServiceTemplateItem): item is Service { 
    return (item as Service).route !== undefined; 
  }
  
  isServicePlaceholder(item: ServiceTemplateItem): item is { placeholder: true } { 
    return 'placeholder' in item; 
  }

scrollToSection(sectionId: string): void {
  this.loadedSections.add(sectionId);

  setTimeout(() => {
    let element = document.getElementById(sectionId);
    if (!element) {
      element = document.getElementById(sectionId + 'ObserverTarget');
    }

    if (element) {
      const rect = element.getBoundingClientRect();

      let offset =
        rect.top +
        window.pageYOffset -
        (window.innerHeight / 2) +
        (rect.height / 2);

      // ⭐ Prevent overscrolling beyond page bottom
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;

      offset = Math.min(offset, maxScroll);

      window.scrollTo({
        top: offset,
        behavior: 'smooth'
      });

      this.activeSection = sectionId;
    }
  }, 50);
}

  // ================= INDUSTRIES HOVER CONTROL =================
  pauseIndustrySlider(): void {
    this.clearAutoSlide();
  }

  resumeIndustrySlider(): void {
    if (this.industriesVisible && !this.slideInterval) {
      this.startAutoSlide();
    }
  }

  // ================= SERVICES HOVER CONTROL =================
  pauseServiceSlider(): void {
    this.clearAutoServiceSlide();
  }

  resumeServiceSlider(): void {
    if (this.servicesInitialized && this.servicesVisible && !this.serviceSlideInterval) {
      this.startAutoServiceSlide();
    }
  }

  openContactModal() {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  goToCaseStudies(): void {
    this.router.navigate(['/kalpita-case-studies/case-study']);
  }

  ExploreAboutUs(): void {
    this.router.navigate(['/aboutcompany/about-kalpita-technologies']);
  }
}